import React from 'react';

import {
  AlertDialog,
  Box,
  Header,
  addWithConfig,
  makeUclComponent,
} from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import noop from 'utils/noop';

import { AlertDialogEventLogger } from './alert-dialog-event-logger';
import type { IDialogProps } from './types';

const CloseButtonContainer = Box.withConfig({
  mt: '$2',
  width: 'full',
});

export const CloseButton = makeUclComponent(ActionButton).withConfig({
  fullWidth: true,
  variant: ActionButtonVariants.TEXT_ONLY,
});

const Dialog: React.FC<React.PropsWithChildren<IDialogProps>> = ({
  children,
  heading,
  headingComponent,
  body,
  bodyComponent,
  onDismiss,
  actions,
  image,
  initialFocusRef,
  /**
   * A small string sent to amplitude describing the purpose of the modal.
   */
  modalAppearanceEventMessage,
  /**
   * Additional Error details to be sent to amplitude.
   */
  errorEventMessage,
  testID,
  showCloseButton = false,
  showDialog,
  confirmButtonLabel,
  closeButtonLabelId = 'close',
  confirmDialog = noop,
  maxHeight,
  contentWidth,
  contentMaxWidth,
  marginBottom,
  contentPadding,
  ...props
}) => {
  const { formatMessage } = useIntl();

  if (confirmButtonLabel && !actions) {
    actions = (
      <ActionButton
        fullWidth={showCloseButton}
        onPress={confirmDialog}
        testID="dialog-confirm-btn"
        variant={showCloseButton ? ActionButtonVariants.PRIMARY : ActionButtonVariants.TEXT_ONLY}
      >
        {confirmButtonLabel}
      </ActionButton>
    );
  }

  if (!actions && !showCloseButton && __DEV__) {
    // eslint-disable-next-line no-console
    console.error('Dialog: Please add actions or show the close button (showCloseButton)');
  }
  const cancelRef = React.useRef(null);

  // The dialog content will respect its default maximum height unless you specify a different maxHeight value.
  const dialogContentMaxHeight = { ...(maxHeight && { maxHeight }) };
  const dialogContentWidth = {
    ...(contentWidth && {
      width: contentWidth,
    }),
  };

  return (
    <AlertDialogEventLogger
      modalAppearanceEventMessage={modalAppearanceEventMessage}
      errorEventMessage={errorEventMessage}
      modalAppearanceHeader={heading}
      modalAppearanceMessage={body}
      leastDestructiveRef={initialFocusRef || cancelRef}
      isOpen={showDialog}
      onClose={onDismiss}
      {...props}
    >
      <AlertDialog.Content
        {...dialogContentWidth}
        {...dialogContentMaxHeight}
        testID={testID}
        maxWidth={contentMaxWidth}
        marginBottom={marginBottom}
        padding={contentPadding}
      >
        {image}
        {headingComponent ? (
          headingComponent
        ) : heading ? (
          <AlertDialog.Header accessibilityLabel={`${heading}: ${body}`}>
            <Header variant="headerTwo" accessibilityHidden>
              {heading}
            </Header>
          </AlertDialog.Header>
        ) : null}
        {children ? (
          children
        ) : bodyComponent ? (
          bodyComponent
        ) : body ? (
          <AlertDialog.Body>{body}</AlertDialog.Body>
        ) : null}
        {(actions || showCloseButton) && (
          <AlertDialog.Footer>
            {actions}
            {showCloseButton && (
              <CloseButtonContainer>
                <CloseButton onPress={onDismiss}>
                  {formatMessage({ id: closeButtonLabelId })}
                </CloseButton>
              </CloseButtonContainer>
            )}
          </AlertDialog.Footer>
        )}
      </AlertDialog.Content>
    </AlertDialogEventLogger>
  );
};

export default addWithConfig(React.memo(Dialog));
